/**
 * Feature Access Types
 *
 * Types for the feature wall/paywall system that controls access to features
 * based on subscription tier and user role.
 */

import type { MessagingPlatform } from "./messaging";

// Subscription tiers (no custom tier currently)
export type SubscriptionTier =
  | "trial"
  | "independent"
  | "business_essentials"
  | "business_growth"
  | "medspa_essentials";

export type UserLevel = "Owner" | "Manager" | "Employee" | "Assistant";

export type FeatureKey =
  | "templated_website"
  | "online_booking"
  | "calendar"
  | "crm"
  | "inventory"
  | "deposits"
  | "reports"
  | "marketing"
  | "payments"
  | "payroll"
  | "basic_ai"
  | "ai_agents"
  | "loyalty"
  | "client_groups";

export interface OnboardingState {
  initial_onboarding: "null" | "inprogress" | "complete";
  initial_onboarding_stage: string | null;
  progressive_employees: "null" | "inprogress" | "complete";
  progressive_services: "null" | "inprogress" | "complete";
  progressive_payments: "null" | "inprogress" | "complete";
}

export type UnlockedFeatures = Record<FeatureKey, boolean>;

// Section permission types - matches backend sectionPermissions response
export type SectionScope = "own" | "all";
export type SectionAccess = "none" | "view" | "own" | "all";

/**
 * Base interface for section permissions with the new 'available' flag
 */
interface BaseSectionPermission {
  /** Whether this workflow is available based on subscription tier */
  available: boolean;
  scope: SectionScope;
  access: SectionAccess;
}

export interface CalendarPermissions extends BaseSectionPermission {
  canManageTimeBlocks: boolean;
  canDeleteTimeBlocks: boolean;
  canManageAppointmentTags: boolean;
}

export interface ProductsPermissions extends BaseSectionPermission {
  canView: boolean;
  canEdit: boolean;
}

export interface ClientsPermissions extends BaseSectionPermission {
  canView: boolean;
  canDelete: boolean;
}

export interface PackagesPermissions extends BaseSectionPermission {
  /** Can add, adjust units, edit expiry, and revoke a client's service packages */
  canManageClientPackages: boolean;
}

export interface GiftCardsPermissions extends BaseSectionPermission {
  /** Add value (grant) and adjust (grant/detract) a client's gift cards. */
  canManageClientGiftCards: boolean;
  /** Void a client's gift card (writes off remaining balance). Owner/Manager only. */
  canVoidGiftCards: boolean;
}

export interface MarketingPermissions extends BaseSectionPermission {
  canView: boolean;
  canCreate: boolean;
  canCancel: boolean;
}

export interface MessagingPermissions extends BaseSectionPermission {
  /** See the Chat inbox + read threads / mark read-unread. */
  canView: boolean;
  /** Send interactive replies + link/assign a client. */
  canSend: boolean;
}

export interface ReportsPermissions extends Omit<
  BaseSectionPermission,
  "scope"
> {
  scope: SectionScope | "none";
  canDownload: boolean;
  /** Sales Summary access. Absent from older backends. */
  canViewSalesSummary?: boolean;
  /** Earnings Summary access. Absent from older backends. */
  earningsSummaryScope?: SectionScope | "none";
  /** Share saved reports with other staff. Absent from older backends. */
  canShareReports?: boolean;
}

export interface SettingsStaffPermissions extends BaseSectionPermission {
  canEdit: boolean;
}

export interface NotificationsPermissions extends BaseSectionPermission {}

export interface SettingsNotificationsPermissions extends Omit<
  BaseSectionPermission,
  "scope"
> {
  scope: SectionScope | "none";
}

export interface SettingsStudioPermissions extends BaseSectionPermission {
  canView: boolean;
  canEdit: boolean;
  canSeeBilling: boolean;
}

export interface SettingsServicesPermissions extends BaseSectionPermission {
  canManageServices: boolean;
  canViewAdjustmentForms: boolean;
  canManageAdjustmentForms: boolean;
}

export interface SettingsSSGPermissions extends BaseSectionPermission {
  canAccess: boolean;
}

export interface SettingsDepositsPermissions extends BaseSectionPermission {
  canAccess: boolean;
}

export interface SettingsGiftCardsPermissions extends BaseSectionPermission {
  canAccess: boolean;
}

export interface SettingsDiscountsPermissions extends BaseSectionPermission {
  /** Create and edit discount presets (Settings + inline create on a ticket). */
  canManagePresets: boolean;
  /** Delete discount presets (used ones are archived by the backend). */
  canDeletePresets: boolean;
}

export interface SettingsCommissionsPermissions extends Omit<
  BaseSectionPermission,
  "scope"
> {
  scope: SectionScope | "none";
  canEdit: boolean;
  /** Can assign/reassign the commission earner on a product line item. */
  canAssignProductCommission: boolean;
}

export interface SettingsPayrollPermissions extends BaseSectionPermission {
  canAccess: boolean;
}

export interface StaffTiersPermissions extends BaseSectionPermission {
  /** Create/rename/reorder tiers + manage a tier's service/commission defaults. */
  canCreateTiers: boolean;
  /** Assign/move/remove staff to/from a tier + apply (reset) defaults onto them. */
  canAssignStaffToTiers: boolean;
  /** Delete a tier (only when it has no members). */
  canDeleteTiers: boolean;
}

export interface PaymentsPermissions extends BaseSectionPermission {
  canView: boolean;
  /** Can issue partial/full refunds against accepted payments. */
  canIssueRefunds: boolean;
  /** Can add, remove, and rename payment terminals (card readers). */
  canManageTerminals: boolean;
}

export interface CashRegisterPermissions extends BaseSectionPermission {
  canView: boolean;
  canManage: boolean;
}

export interface PayrollPermissions extends BaseSectionPermission {
  clockScope: SectionScope;
  canSyncToPayroll: boolean;
}

export interface FormsAndWaiversPermissions extends Omit<
  BaseSectionPermission,
  "scope" | "access"
> {
  scope: SectionScope | "none";
  access: SectionAccess;
  canViewClientForms: boolean;
  canManageClientForms: boolean;
  canViewTemplates: boolean;
  canManageTemplates: boolean;
  /** Echoed for PHI workflows; informational. Salons whose business_vertical
   * doesn't match get available:false and all canX:false. */
  required_business_vertical?: string;
}

export interface SalonDocumentsPermissions extends Omit<
  BaseSectionPermission,
  "scope" | "access"
> {
  scope: SectionScope | "none";
  access: SectionAccess;
  canViewDocuments: boolean;
  canManageDocuments: boolean;
  required_business_vertical?: string;
}

export interface SectionPermissions {
  calendar: CalendarPermissions;
  products: ProductsPermissions;
  clients: ClientsPermissions;
  packages: PackagesPermissions;
  giftCards: GiftCardsPermissions;
  marketing: MarketingPermissions;
  messaging: MessagingPermissions;
  reports: ReportsPermissions;
  payroll: PayrollPermissions;
  payments: PaymentsPermissions;
  cashRegister: CashRegisterPermissions;
  notifications: NotificationsPermissions;
  settingsNotifications: SettingsNotificationsPermissions;
  settingsStudio: SettingsStudioPermissions;
  settingsStaff: SettingsStaffPermissions;
  settingsServices: SettingsServicesPermissions;
  settingsSSG: SettingsSSGPermissions;
  settingsDeposits: SettingsDepositsPermissions;
  settingsGiftCards: SettingsGiftCardsPermissions;
  settingsDiscounts: SettingsDiscountsPermissions;
  settingsCommissions: SettingsCommissionsPermissions;
  settingsPayroll: SettingsPayrollPermissions;
  staffTiers: StaffTiersPermissions;
  formsAndWaivers: FormsAndWaiversPermissions;
  salonDocuments: SalonDocumentsPermissions;
}

export type SectionKey = keyof SectionPermissions;

export type BusinessVertical =
  | "hair"
  | "nail"
  | "barber"
  | "aesthetician"
  | "med_spa";

/**
 * One location a staff member is assigned to. Emitted by `/access` only when
 * the backend `MULTI_LOCATION_READS_ENABLED` flag is ON (PR A / #399). Field
 * names are `salon`-scoped verbatim — the DB/API rename is a separate future
 * PR; do NOT rename to `business_location_*` here.
 */
export interface AvailableLocation {
  salon_id: number;
  salon_uuid: string;
  name: string;
  street_address_1: string | null;
  city: string | null;
  state: string | null;
  /** This human's role at this location (may differ per location). */
  role: UserLevel;
}

export interface FeatureAccessResponse {
  user_level: UserLevel;
  subscription_tier: SubscriptionTier;
  subscription_tier_level: number;
  trial_end_dt: string | null;
  number_of_active_employees: number;
  max_allowed_employees: number;
  onboarding: OnboardingState;
  unlocked_features: UnlockedFeatures;
  section_permissions: SectionPermissions | null;
  can_add_employees: boolean;
  employees_remaining: number;
  business_vertical?: BusinessVertical | null;
  staffing_model?: string | null;
  /**
   * This location's SMS/MMS platform. The Chat inbox surfaces only when this is
   * `"twilio"` AND `section_permissions.messaging.canView` is true; absent /
   * `"end_user_messaging"` locations have no inbox. `normalizeAccessResponse`
   * defaults it to `null`.
   */
  messaging_platform?: MessagingPlatform | null;
  /**
   * Multi-location fields — present only when the backend flag is ON. When the
   * flag is OFF these are absent; `normalizeAccessResponse` defaults them to
   * `[]` / `null` so the app degrades to single-location behavior.
   */
  available_locations: AvailableLocation[];
  selected_salon_id: number | null;
  /**
   * uuid of `selected_salon_id`: this tab's own location (echoed from its
   * `X-Business-Location-Id` header) when still assigned, else the remembered
   * landing location. `null` on first sign-in or from an older backend.
   */
  selected_salon_uuid: string | null;
}

export interface FeatureAccessError {
  type:
    | "network"
    | "auth"
    | "payment_required"
    | "feature_locked"
    | "employee_limit"
    | "unknown";
  message: string;
  details?: {
    required_tier?: SubscriptionTier;
    current_tier?: SubscriptionTier;
    feature?: FeatureKey;
    trial_end_dt?: string;
    subscription_status?: string;
    current_count?: number;
    max_allowed?: number;
  };
}

// Tier configuration (14-day trial)
export const TIER_CONFIG: Record<
  SubscriptionTier,
  {
    level: number;
    maxEmployees: number;
    price: number;
    displayName: string;
    trialDays?: number;
  }
> = {
  trial: {
    level: 0,
    maxEmployees: 1,
    price: 0,
    displayName: "Trial",
    trialDays: 14,
  },
  independent: {
    level: 1,
    maxEmployees: 1,
    price: 9900,
    displayName: "Independent",
  },
  business_essentials: {
    level: 2,
    maxEmployees: 5,
    price: 15900,
    displayName: "Business Essentials",
  },
  business_growth: {
    level: 3,
    maxEmployees: 15,
    price: 23900,
    displayName: "Business Growth",
  },
  medspa_essentials: {
    // MedSpa-restricted tier; ranked at the Essentials level for feature gating.
    level: 2,
    maxEmployees: 5,
    // Variable per-salon pricing; not used for display.
    price: 0,
    displayName: "MedSpa Essentials",
  },
};

// Feature to minimum tier mapping
export const FEATURE_TIER_MAP: Record<FeatureKey, SubscriptionTier> = {
  templated_website: "trial",
  online_booking: "trial",
  calendar: "trial",
  crm: "trial",
  inventory: "trial",
  deposits: "trial",
  reports: "trial",
  marketing: "independent",
  payments: "independent",
  payroll: "independent",
  basic_ai: "business_essentials",
  ai_agents: "business_growth",
  // Loyalty rollout tier TBD; dev-only via FeatureAccessContext until the
  // backend /access response carries it. Surfaces hide entirely when locked,
  // so this mapping is informational for now.
  loyalty: "business_essentials",
  // Client Groups rollout tier TBD; dev-only via FeatureAccessContext until the
  // backend /access response carries it (mirrors loyalty). Informational for now.
  client_groups: "independent",
};

// Feature display names for UI
export const FEATURE_DISPLAY_NAMES: Record<FeatureKey, string> = {
  templated_website: "Templated Website",
  online_booking: "Online Booking",
  calendar: "Calendar",
  crm: "CRM",
  inventory: "Inventory",
  deposits: "Deposits",
  reports: "Reports",
  marketing: "Marketing",
  payments: "Payments",
  payroll: "Payroll",
  basic_ai: "Basic AI Features",
  ai_agents: "AI Agents",
  loyalty: "Rewards",
  client_groups: "Client Groups",
};

/**
 * Helper to get the next tier up from the current tier.
 *
 * Pass the salon's `businessVertical` so vertical-restricted tiers
 * (e.g. `medspa_essentials`, only available to `med_spa` salons) are only
 * suggested to salons that can actually subscribe to them. Without a
 * vertical, vertical-restricted tiers are filtered out so callers never
 * recommend an unreachable upgrade.
 */
export const getNextTier = (
  currentTier: SubscriptionTier,
  businessVertical?: BusinessVertical | null,
): SubscriptionTier | null => {
  const fullTierOrder: SubscriptionTier[] = [
    "trial",
    "independent",
    "business_essentials",
    "medspa_essentials",
    "business_growth",
  ];
  // Which tiers are gated to a specific vertical (must mirror
  // SUBSCRIPTION_TIERS[].requiredBusinessVertical in types/subscription.ts).
  const tierRequiredVertical: Partial<
    Record<SubscriptionTier, BusinessVertical>
  > = {
    medspa_essentials: "med_spa",
  };
  const tierOrder = fullTierOrder.filter((tier) => {
    const required = tierRequiredVertical[tier];
    if (!required) return true;
    return businessVertical === required;
  });
  const currentIndex = tierOrder.indexOf(currentTier);
  if (currentIndex === -1 || currentIndex === tierOrder.length - 1) {
    return null;
  }
  return tierOrder[currentIndex + 1];
};

/**
 * Helper to check if a tier meets the minimum requirement
 */
export const tierMeetsRequirement = (
  currentTier: SubscriptionTier,
  requiredTier: SubscriptionTier,
): boolean => {
  return TIER_CONFIG[currentTier].level >= TIER_CONFIG[requiredTier].level;
};
